import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { KeycloakAuthService } from '../services/keycloak-auth.service';
import axios from 'axios';

/**
 * HTTP interceptor that attaches the Keycloak access token to outgoing requests.
 * The token is added as a Bearer token in the Authorization header.
 * This also configures Axios to automatically include the token in all requests.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  const authService = inject(KeycloakAuthService);

  // Only intercept requests in the browser
  if (!isPlatformBrowser(platformId)) {
    return next(req);
  }

  // Skip token attachment for certain URLs (like the OAuth endpoints)
  const skipUrls = [
    '/realms/',
    '.well-known/openid-configuration',
    '/protocol/openid-connect/'
  ];

  if (skipUrls.some(url => req.url.includes(url))) {
    return next(req);
  }

  // Get the access token
  const token = authService.accessToken;

  if (token) {
    // Clone the request and add the Authorization header for Angular HTTP
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });

    // Also configure Axios with the token for all requests
    configureAxiosInterceptor(token);

    return next(authReq);
  }

  return next(req);
};

/**
 * Configure Axios request/response interceptors to automatically include the OAuth token
 */
let axiosInterceptorsConfigured = false;

function configureAxiosInterceptor(token: string): void {
  // Only configure once
  if (axiosInterceptorsConfigured) {
    return;
  }
  axiosInterceptorsConfigured = true;

  // Request interceptor: Add token to all requests
  axios.interceptors.request.use(
    (config) => {
      // Skip token for OAuth/Keycloak endpoints
      const skipUrls = [
        '/realms/',
        '.well-known/openid-configuration',
        '/protocol/openid-connect/'
      ];

      if (!skipUrls.some(url => config.url?.includes(url))) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle 401 Unauthorized responses
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.warn('Unauthorized request - token may have expired');
        // Token refresh will be handled by the Keycloak service
      }
      return Promise.reject(error);
    }
  );
}
